import * as React from 'react';
import { UtaPolicyAssessmentType, IUtaPolicy, UtaPolicyValueSeat } from 'obt-common';
import Cancellation from '@spotnana/pixel-react/dist/Icons/Cancellation';
import Typography from '@spotnana/pixel-react/dist/Typography';
import { IconProps } from '@spotnana/pixel-react/dist/Icons/common';
import { UtaPolicy } from 'obt-common/types/api/v1/obt/air/routehappy';
import { useBlocksTheme } from '@spotnana/blocks/src/utils/styling/useBlocksTheme';
import UtaTooltip from 'src/app/flights/FlightSearchResultsPage/AmenitiesUtaInfoItem/UtaTooltip';
import { Icon } from '@spotnana/blocks/src/Icon';
import iconMap, { seatIconMap } from '../../../../../../constants/iconMap';
import { uta_container } from './styles';

interface IUtaPolicyInfoItemProps {
  utaKey: string;
  utaDisplayText?: string;
  utaPolicy?: IUtaPolicy;
  isDisabled?: boolean;
  dataTestId?: string;
  icon?: React.FC<IconProps>;
  textColor?: string;
  variant: 'default' | 'small';
}

interface IAmenityInfoItemProps {
  amenityKey: string;
  amenityDisplayText: string;
  dataTestId?: string;
  icon?: React.FC<IconProps>;
  textColor?: string;
  variant: 'default' | 'small';
}

function getIsRestricted(utaPolicyData?: UtaPolicy): boolean {
  return utaPolicyData?.assessmentType === UtaPolicyAssessmentType.RESTRICTION;
}

type UTARendererProps = {
  utaKey: string;
  description: string | undefined;
  isRestricted: boolean;
  _isDisabled: boolean;
  utaPolicyValue: UtaPolicy | undefined;
  dataTestId?: string;
  icon?: React.FC<IconProps>;
  textColor?: string;
  variant: 'default' | 'small';
};

const UtaPolicyItem = ({
  utaKey,
  description,
  isRestricted,
  _isDisabled,
  utaPolicyValue,
  dataTestId,
  icon,
  textColor,
  variant,
}: UTARendererProps) => {
  const { palette } = useBlocksTheme();
  if (!description) {
    return <></>;
  }
  let textLabelColor = textColor || palette.text.secondary;
  let UtaIcon;
  if (icon) {
    UtaIcon = icon;
  } else if (isRestricted) {
    UtaIcon = Cancellation;
  } else {
    UtaIcon = utaKey === 'seatType' ? seatIconMap[utaPolicyValue?.value?.seat as UtaPolicyValueSeat] : iconMap[utaKey];
  }

  if (_isDisabled || isRestricted) {
    textLabelColor = palette.text.secondary;
  }
  const isSmall = variant === 'small';
  const size = isSmall ? '20px' : '24px';
  const blockIconSize = isSmall ? 20 : 24;

  return (
    <div css={uta_container(isSmall)} data-testid={dataTestId}>
      {utaKey === 'overheadBag' || utaKey === 'security' ? (
        <Icon
          name={utaKey === 'overheadBag' ? 'LuggageCarryOn' : 'WebCheckin'}
          height={blockIconSize}
          width={blockIconSize}
          color={textLabelColor}
        />
      ) : (
        <div>
          <UtaIcon width={size} height={size} primaryColor={textLabelColor} style={{ minWidth: size }} />
        </div>
      )}

      <Typography lineHeight="20px" ml={isSmall ? '8px' : '14px'} as="div" variation="small" color={textLabelColor}>
        {description}
      </Typography>
      <UtaTooltip utaInfoTextKey={utaPolicyValue?.summary?.info ?? null} />
    </div>
  );
};

function UtaPolicyInfoItem({
  utaKey,
  utaDisplayText,
  utaPolicy,
  isDisabled = false,
  dataTestId,
  icon,
  textColor,
  variant,
}: IUtaPolicyInfoItemProps): JSX.Element {
  const isRestricted = getIsRestricted(utaPolicy);
  return (
    <UtaPolicyItem
      {...{
        utaKey,
        description: utaDisplayText,
        isRestricted,
        _isDisabled: isDisabled,
        utaPolicyValue: utaPolicy,
        dataTestId,
        icon,
        textColor,
        variant,
      }}
    />
  );
}

function AmenityInfoItem({
  amenityKey,
  amenityDisplayText,
  dataTestId,
  icon,
  textColor,
  variant,
}: IAmenityInfoItemProps): JSX.Element {
  return (
    <UtaPolicyItem
      {...{
        utaKey: amenityKey,
        description: amenityDisplayText,
        isRestricted: false,
        _isDisabled: false,
        utaPolicyValue: undefined,
        dataTestId,
        icon,
        textColor,
        variant,
      }}
    />
  );
}

export { UtaPolicyInfoItem, AmenityInfoItem };
